import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { AppState } from 'react-native';
import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { useDetailQuery } from '../../common/containers/helpersDetailContainer';
import {
	MODIFY_USER, MODIFY_USER_SETTINGS, MODIFY_USER_SETTING,
} from '../../common/graphql/user';
import { MODIFY_TENANT, MODIFY_TENANT_SETTINGS } from '../../common/graphql/tenant';
import { UserContext } from '../../common/utils/context';
import {
	canCompleteAssignedMarketSetups as userCanCompleteAssignedMarketSetups,
	isDriver,
	isPicker,
	isTechnician,
} from '../../common/utils/user/roleCheck';
import {
	isBitconnect, isEamonn, isAVC,
} from '../../common/utils/user/userTenantCheck';
import { NO_PERMISSION_REASON, SCOPING_MODEL_NAME } from '../../common/constants';
import { Alert } from '../components/other/Alert';
import { GET_MOBILE_USER } from '../graphql/user';
import useExtraAccessUser from '../../common/utils/hooks/useExtraAccessUser';

// This is a direct implementation of withUser as a provider component
// to avoid HOC evaluation at module load time
const UserProvider = ({ children }) => {
	const isAuthenticated = useSelector(state => state.auth.isAuthenticated);

	const {
		data: queriedUser, loading, error, refetch,
	} = useDetailQuery(GET_MOBILE_USER, undefined, {
		fetchPolicy: 'network-only',
		skip: !isAuthenticated,
	});
	const user = useExtraAccessUser(queriedUser, refetch);

	// Picks up access changes made while the app was open, such as extra access given or removed.
	useEffect(() => {
		if (!isAuthenticated) {
			return undefined;
		}

		const subscription = AppState.addEventListener('change', (nextAppState) => {
			if (nextAppState === 'active') {
				refetch().catch(() => {});
			}
		});
		return () => subscription?.remove?.();
	}, [isAuthenticated, refetch]);

	const isProfileErrorVisible = useRef(false);
	const [modifyUserRaw] = useMutation(MODIFY_USER);
	const [modifyUserSettingsRaw] = useMutation(MODIFY_USER_SETTINGS);
	const [modifyUserSettingRaw] = useMutation(MODIFY_USER_SETTING);
	const [modifyTenantRaw] = useMutation(MODIFY_TENANT);
	const [modifyTenantSettingsRaw] = useMutation(MODIFY_TENANT_SETTINGS);

	const modifyUser = useCallback(data => modifyUserRaw({
		variables: {
			input: {
				id: user.id,
				...data,
			},
		},
	}).catch(() => {
		if (isProfileErrorVisible.current) {
			return;
		}
		isProfileErrorVisible.current = true;
		// Focusing the alert can blur an autosave input and retry the same failed change.
		Alert.alert('Profile not saved', 'Your changes could not be saved. Check your connection and try again.', [{
			text: 'OK',
			onPress: () => { isProfileErrorVisible.current = false; },
		}], { cancelable: false });
	}), [modifyUserRaw, user]);

	const modifyUserSettings = useCallback(values => {
		return modifyUserSettingsRaw({
			variables: {
				id: user.id,
				settings: {
					...user.settings,
					...values,
				},
			},
		});
	}, [modifyUserSettingsRaw, user]);

	const modifyUserSetting = useCallback(values => {
		return modifyUserSettingRaw({
			variables: {
				id: user.id,
				settings: values,
			},
		});
	}, [modifyUserSettingRaw, user]);

	const modifyTenantSettings = useCallback(values => {
		return modifyTenantSettingsRaw({
			variables: {
				input: {
					id: user.tenant.id,
					...values,
				},
			},
		});
	}, [modifyTenantSettingsRaw, user]);

	const modifyTenant = useCallback(values => {
		return modifyTenantRaw({
			variables: {
				input: {
					id: user.tenant.id,
					...values,
				},
			},
		});
	}, [modifyTenantRaw, user]);

	const hasPermission = useCallback((permissionId, document, field) => {
		if (!user) {
			return false;
		}

		if (user.isSuperAdmin) {
			return true;
		}

		const userHasPermission = user.role.permissions.includes(permissionId);
		const tenantHasPermission = true;
		const tenantCanOverride = Boolean(document && field && (
			(document.__typename === 'Location' && field === 'person') ||
			(document.__typename === 'Client' && ['person', 'locations'].includes(field))));

		return userHasPermission && (tenantHasPermission || tenantCanOverride);
	}, [user]);

	const hasPermissionWithReason = useCallback((permissionId, document, field) => {
		if (!user) {
			return [false, NO_PERMISSION_REASON.NO_USER];
		}

		if (user.isSuperAdmin) {
			return [true];
		}

		const userHasPermission = user.role.permissions.includes(permissionId);

		if (!userHasPermission) {
			return [false, NO_PERMISSION_REASON.NO_ROLE_PERMISSION];
		}

		const tenantHasPermission = true;
		const tenantCanOverride = Boolean(document && field && (
			(document.__typename === 'Location' && field === 'person') ||
			(document.__typename === 'Client' && ['person', 'locations'].includes(field))));

		const tenantHasPermissionFinal = tenantHasPermission || tenantCanOverride;

		if (tenantHasPermissionFinal) {
			return [true];
		}
		else {
			return [false, NO_PERMISSION_REASON.NOT_ROOT_TENANT];
		}
	}, [user]);

	const isScoped = useCallback(modelName => {
		return !modelName
			? Boolean(user?.scopingModel) || Boolean(user?.role?.viewDataDriverIsAssignedTo)
			: user?.scopingModel === modelName || (modelName === SCOPING_MODEL_NAME.ROUTE && user?.role?.viewDataDriverIsAssignedTo);
	}, [user?.scopingModel, user?.role?.viewDataDriverIsAssignedTo]);

	const userContextValues = useMemo(() => ({
		user,
		userSettings: user && user.settings,
		tenant: user && user.tenant,
		tenantSettings: user && user.tenantSettings,
		modifyUser,
		modifyTenantSettings,
		modifyUserSetting,
		modifyUserSettings,
		modifyTenant,
		loading,
		error,
		refetch,
		canCompleteAssignedMarketSetups: Boolean(user && userCanCompleteAssignedMarketSetups(user)),
		isDriver: user && isDriver(user),
		isPicker: user && isPicker(user),
		isTechnician: user && isTechnician(user),
		isEamonn: user && isEamonn(user),
		isBitconnect: user && isBitconnect(user),
		isAVC: user && isAVC(user.tenant),
		hasPermission,
		hasPermissionWithReason,
		isScoped,
		isSuperAdmin: user && user.isSuperAdmin,
		canViewClientsLeads: Boolean(user?.clientsLeadAccess?.canView),
		canManageClientsLeads: Boolean(user?.clientsLeadAccess?.canManage),
		masterTenantManagesMachines: user?.tenant?.masterTenant?.tenantSettings?.managesSlaveMachines,
		masterTenantManagesCommissions: user?.tenant?.masterTenant?.tenantSettings?.managesSlaveCommissions,
		masterTenantSettings: user?.tenant?.masterTenant?.tenantSettings,
	}), [user, modifyUser, modifyTenantSettings, modifyUserSetting, modifyUserSettings, modifyTenant, loading, error, refetch, hasPermissionWithReason, hasPermission, isScoped]);

	return (
		<UserContext.Provider value={userContextValues}>
			{children}
		</UserContext.Provider>
	);
};

export default UserProvider;
